import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Cookies from "js-cookie";
import { ArrowTopRightOnSquareIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { Card, Button, Title } from "@yoast/ui-library";
import { defineMessages, FormattedMessage } from "react-intl";
import {
	SURVEY_URL,
	SURVEY_NOTIFICATION_COOKIE,
	SURVEY_COOKIE_EXPIRY_DAYS,
	ENABLE_SURVEY,
	SURVEY_RUNS_UNTIL,
} from "../../config/surveyConfig";
import yoastIcon from "../../icons/yoast-icon.svg";
import * as styles from "./styles.module.scss";

const messages = defineMessages( {
	title: {
		id: "surveyNotification.title",
		defaultMessage: "Help us improve our license management",
	},
	description: {
		id: "surveyNotification.description",
		defaultMessage: "We're exploring new features and pricing tailored for multi-site users and agencies. Please tell us a bit about your business in this 3-minute survey.",
	},
	takeSurvey: {
		id: "surveyNotification.takeSurvey",
		defaultMessage: "Take quick survey",
	},
	noThanks: {
		id: "surveyNotification.noThanks",
		defaultMessage: "No, thanks",
	},
} );

/**
 * Dismissible survey notification component.
 *
 * Shows a notification asking users to complete a survey.
 * Once dismissed, it won't reappear for the duration configured in SURVEY_COOKIE_EXPIRY_DAYS (10 years by default).
 *
 * @returns {JSX.Element|null} The survey notification or null if hidden.
 */
const SurveyNotification = ( { onDismiss = null, isEligible = false } ) => {
	const [ isHidden, setIsHidden ] = useState( true );

	useEffect( () => {
		const wasDismissed = Cookies.get( SURVEY_NOTIFICATION_COOKIE );
		const isExpired = SURVEY_RUNS_UNTIL && new Date() > SURVEY_RUNS_UNTIL;
		setIsHidden( !! wasDismissed || ! ENABLE_SURVEY || isExpired || ! isEligible );
	}, [ isEligible ] );

	const handleDismiss = useCallback( () => {
		Cookies.set( SURVEY_NOTIFICATION_COOKIE, "true", { expires: SURVEY_COOKIE_EXPIRY_DAYS } );
		setIsHidden( true );
		if ( onDismiss ) {
			onDismiss();
		}
	}, [ onDismiss ] );

	if ( isHidden ) {
		return null;
	}

	return (
		<Card className={ styles.surveyNotificationCard }>
			<XMarkIcon
				className={ styles.closeButton }
				onClick={ handleDismiss }
				aria-label="Close"
				role="button"
				tabIndex={ 0 }
				onKeyDown={ ( e ) => e.key === "Enter" && handleDismiss() }
			/>
			<div className={ styles.container }>
				<div>
					<img src={ yoastIcon } alt="" className={ styles.icon } />
				</div>
				<div className={ styles.content }>
					<Title size={ "5" }>
						<FormattedMessage { ...messages.title } />
					</Title>
					<p>
						<FormattedMessage { ...messages.description } />
					</p>
					<div className={ styles.buttonContainer }>
						<Button
							as="a"
							variant="primary"
							href={ SURVEY_URL }
							target="_blank"
							rel="noopener noreferrer"
							onClick={ handleDismiss }
						>
							<FormattedMessage { ...messages.takeSurvey } />
							<ArrowTopRightOnSquareIcon className={ styles.externalIcon } />
						</Button>
						<Button
							variant="tertiary"
							onClick={ handleDismiss }
						>
							<FormattedMessage { ...messages.noThanks } />
						</Button>
					</div>
				</div>
			</div>
		</Card>
	);
};

SurveyNotification.propTypes = {
	onDismiss: PropTypes.func,
	// Decided by useSurveyEligibility, which owns the licence threshold. Defaults to false so a
	// standalone render stays hidden rather than assuming eligibility.
	isEligible: PropTypes.bool,
};

export default SurveyNotification;
